import { isBefore } from 'owa-datetime';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import getItemIdToUpdate from '../utils/getItemIdToUpdate';
import strings from 'owa-strings/owa-calendar-strings';
import { CalendarEventOperationResult, instantCalendarEventDelete } from '../actions/publicActions';
import { ControlIcons } from 'owa-control-icons';
import { getCalendarEventWithId } from '../selectors/calendarFolderEventsSelectors';
import { CalendarEventEntity } from '../store/schema/CalendarEventEntity';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isEventsCacheInitialized } from '../selectors/eventsCacheSelectors';
import { orchestrator } from 'satcheljs';
import { reloadItemsCache } from '../utils/SharedCacheFunctions';
import { removeCalendarEventsFromEventsCacheMatchingFilter } from '../actions/eventsCacheActions';
import {
    lazyShowCalendarNotification,
    lazyShowCalendarNotificationWithButtonCallback,
    NotificationBarCallbackReason,
} from 'owa-calendar-notification-bar';

orchestrator(instantCalendarEventDelete, async actionMessage => {
    const {
        eventId,
        eventScope,
        dataPoint,
        actionToExecute,
        errorMessage,
        onEventDeleteSuccess,
        onEventDeleteFailed,
        responseSent,
    } = actionMessage;

    const eventToDelete = getCalendarEventWithId(eventId.Id);
    let allowLocalDelete = isInstantDeleteSupported(eventToDelete);
    onDeleteItemStart(eventToDelete, eventScope, allowLocalDelete);

    if (dataPoint) {
        dataPoint.markUserPerceivedTime(); // mark user perceived time as soon as local lie is done
    }

    // Execute the operation
    let idToDelete = getItemIdToUpdate(eventId.Id, eventScope);
    let actionResult: CalendarEventOperationResult = null;

    try {
        if (isFeatureEnabled('cal-surface-notificationBar')) {
            // this notification gives the user a chance to cancel their action before actually sending to the server
            // if shouldUndo returns true, we return back without calling actionToExecute
            let shouldUndo = await showUndoNotification(eventToDelete, responseSent);
            if (shouldUndo) {
                undoDelete(eventToDelete, allowLocalDelete);
                return;
            }
        }
        actionResult = await actionToExecute(idToDelete);

        if (!actionResult.isSuccessful) {
            onEventDeleteFailed(errorMessage);
            throw errorMessage;
        }
    } catch (error) {
        let retryFunction = () =>
            instantCalendarEventDelete(
                eventId,
                eventScope,
                null,
                actionToExecute,
                onEventDeleteSuccess,
                onEventDeleteFailed,
                errorMessage,
                responseSent
            );
        onDeleteItemFailed(eventToDelete, errorMessage, allowLocalDelete, retryFunction);
        onEventDeleteFailed(error);
        throw error;
    }

    onEventDeleteSuccess();
});

function isInstantDeleteSupported(item: CalendarEvent): boolean {
    // We do not support instant delete for unselected calendars that have no cache.
    return item && isEventsCacheInitialized(item.ParentFolderId.Id);
}

function onDeleteItemStart(
    itemToDelete: CalendarEvent,
    eventScope: EventScope,
    allowLocalDelete: boolean
) {
    if (allowLocalDelete) {
        // Remove item/series from cache
        const folderId = itemToDelete.ParentFolderId.Id;
        if (
            eventScope == EventScope.AllInstancesInSeries ||
            eventScope == EventScope.ThisAndFollowingInstances
        ) {
            let start = itemToDelete.Start;
            removeCalendarEventsFromEventsCacheMatchingFilter(
                folderId,
                item =>
                    item.InstanceKey == itemToDelete.InstanceKey &&
                    (eventScope == EventScope.AllInstancesInSeries ||
                        (eventScope == EventScope.ThisAndFollowingInstances &&
                            !isBefore(item.Start, start)))
            );
        } else {
            removeCalendarEventsFromEventsCacheMatchingFilter(
                folderId,
                event => event.ItemId.Id === itemToDelete.ItemId.Id
            );
        }
    }
}

function undoDelete(itemToDelete: CalendarEvent, allowLocalDelete: boolean) {
    if (allowLocalDelete) {
        reloadItemsCache(itemToDelete.ParentFolderId.Id);
    }
}

function onDeleteItemFailed(
    itemToDelete: CalendarEvent,
    errorMessage: string,
    allowLocalDelete: boolean,
    retryFunction: () => void
) {
    undoDelete(itemToDelete, allowLocalDelete);

    lazyShowCalendarNotificationWithButtonCallback
        .import()
        .then(showCalendarNotificationWithButtonCallback => {
            showCalendarNotificationWithButtonCallback(
                'instantCalendarEventDeleteFailed',
                errorMessage,
                strings.tryAgainLabel,
                () => retryFunction(),
                ControlIcons.Delete
            );
        });
}

/**
 * Shows a notification telling the user they can undo the action
 * @returns a promise that resolves true if the user wants to undo, false if the notification is dismissed for any other reason
 */
function showUndoNotification(
    eventToDelete: CalendarEventEntity,
    responseSent: boolean
): Promise<boolean> {
    let notificationMessage = strings.calendarNotificationsBarDeleteEvent;
    if (eventToDelete.IsMeeting && responseSent && !eventToDelete.IsCancelled) {
        notificationMessage = eventToDelete.IsOrganizer
            ? strings.calendarNotificationsBarCancelMeeting
            : strings.calendarNotificationsBarDeclineMeeting;
    }

    return new Promise<boolean>((resolve, reject) => {
        lazyShowCalendarNotification.import().then(showCalendarNotification => {
            showCalendarNotification(
                'instantCalendarEventDelete',
                notificationMessage,
                {
                    icon: ControlIcons.Error,
                },
                {
                    primaryActionText: strings.undo,
                    notificationCallback: reason => {
                        if (reason === NotificationBarCallbackReason.PrimaryActionClicked) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    },
                }
            );
        });
    });
}
